import { z } from "zod";

export const AUDIENCE_STATUS_VALUES = [
	"IN_PROGRESS",
	"READY",
	"FAILED",
	"EXPIRED",
	"INACTIVE",
	"ACTIVATING",
] as const;

export const audienceSearchSchema = z
	.object({
		address: z.coerce.string().optional(),
		memberSinceMin: z.coerce.string().optional(),
		memberSinceMax: z.coerce.string().optional(),
		eventCategoryId: z.coerce.number().int().optional(),
		eventOccasionId: z.coerce.number().int().optional(),
		participationStatus: z
			.enum(["participate", "reserve", "cancel"])
			.optional(),
		occurrenceStartAt: z.coerce.string().optional(),
		occurrenceEndAt: z.coerce.string().optional(),
		isCampaign: z.coerce.boolean().optional(),
		hasWon: z.coerce.boolean().optional(),
		candidateAtMin: z.coerce.string().optional(),
		candidateAtMax: z.coerce.string().optional(),
		createdAtMin: z.coerce.string().optional(),
		createdAtMax: z.coerce.string().optional(),
		lastVisitMin: z.coerce.string().optional(),
		lastVisitMax: z.coerce.string().optional(),
		messages: z.enum(["read", "unread"]).optional(),
		isFriends: z.coerce.boolean().optional(),
		isRegistered: z.coerce.boolean().optional(),
		friendAddedDateMin: z.coerce.string().optional(),
		friendAddedDateMax: z.coerce.string().optional(),
		membershipStatus: z
			.enum(["provisional", "active", "expired", "deactivated"])
			.optional(),
	})
	.passthrough();

export const audienceCreateSchema = audienceSearchSchema.extend({
	audienceName: z.coerce.string().min(1, "Audience name is required"),
});

export const audienceWithStatusSchema = z.object({
	audienceGroupId: z.coerce.string(),
	description: z.coerce.string().optional(),
	audienceCount: z.coerce.number().int().min(0),
	searchCondition: z.any().nullable().optional(),
	remarks: z.any().nullable().optional(),
	expireTimestamp: z.coerce.number().int().optional(),
	status: z.enum(AUDIENCE_STATUS_VALUES),
	created: z.coerce.number(),
});

export const listAudiencesQueryParamsSchema = z.object({
	type: z
		.enum(["member", "event", "default", "campaign", "survey", "lottery"])
		.optional(),
});

export const listAudiences200Schema = z.object({
	data: z.array(audienceWithStatusSchema),
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean"),
});

export const searchAudienceMutationRequestSchema = audienceSearchSchema;

export const searchAudience200Schema = z.object({
	count: z.coerce.number().int().min(0),
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean"),
});

export const createAudienceMutationRequestSchema = audienceCreateSchema;

export const createAudience200Schema = z.object({
	audienceGroupId: z.coerce.string().optional(),
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean"),
});

export const deleteAudiencePathParamsSchema = z.object({
	audienceGroupId: z.coerce.string(),
});

export const deleteAudience200Schema = z.object({
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean"),
});

export const createAudienceMemberRequestSchema = z.object({
	name: z.coerce.string().min(1),
	audiences: z.array(z.object({ id: z.string() })),
	searchCondition: z.record(z.string(), z.any()),
});

export const createAudienceMember200Schema = z.object({
	audienceGroupId: z.coerce.string(),
	description: z.coerce.string(),
	audienceCount: z.coerce.number().int(),
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean"),
});

export const searchAudienceMembersQueryParamsSchema = audienceSearchSchema;

export const searchAudienceMembers200Schema = z.array(
	z.object({
		id: z.string(),
	}),
);
